import { useEffect, useState } from "react";
import { MARYLINE_AVATAR } from "@/lib/api";
import {
  Eye, Scale, Lightbulb, Flag, MessageCircleHeart, Compass, CheckCircle2, HelpCircle, Quote, ShieldAlert,
} from "lucide-react";

const SECTIONS = [
  { id: "faits", num: "01", title: "Ce que les faits montrent", icon: Eye },
  { id: "faits-vs", num: "02", title: "Faits vs interprétations", icon: Scale },
  { id: "sens", num: "03", title: "Ce que cela peut signifier", icon: Lightbulb },
  { id: "flags", num: "04", title: "Red & Green Flags", icon: Flag },
  { id: "reponse", num: "05", title: "Réponse à ta question", icon: MessageCircleHeart },
  { id: "plan", num: "06", title: "Ton plan d'action", icon: Compass },
  { id: "bilan", num: "07", title: "Ce que nous savons", icon: CheckCircle2 },
];

function SectionNav({ active }) {
  return (
    <nav aria-label="Sections de l'analyse" data-testid="analysis-section-nav"
      className="sticky top-0 z-20 -mx-4 px-4 py-2 bg-[#FAF7F2]/90 backdrop-blur-xl border-b border-[#E8DDD5]">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {SECTIONS.map((s) => (
          <a key={s.id} href={`#${s.id}`} data-testid={`analysis-nav-${s.id}`}
            className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              active === s.id ? "bg-[#6E2D38] text-white border-[#6E2D38]" : "bg-white text-[#5C4A45] border-[#E8DDD5] hover:border-[#C48B92]"
            }`}>
            <span className="font-semibold mr-1.5 opacity-70">{s.num}</span>{s.title}
          </a>
        ))}
      </div>
    </nav>
  );
}

function Section({ meta, tint = "#F2E9E1", lead, children }) {
  const Icon = meta.icon;
  return (
    <section id={meta.id} data-testid={`analysis-section-${meta.id}`} className="scroll-mt-16 animate-fade-up">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: tint }}>
          <Icon className="w-5 h-5 text-[#6E2D38]" strokeWidth={1.75} />
        </div>
        <div>
          <p className="text-[11px] tracking-[0.2em] uppercase text-[#B08A82] font-semibold">Partie {meta.num}</p>
          <h2 className="font-editorial text-2xl sm:text-3xl font-semibold leading-tight text-[#2B1E1B]">{meta.title}</h2>
        </div>
      </div>
      {lead && <p className="text-[15px] text-[#5C4A45] leading-relaxed mb-4">{lead}</p>}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

const Card = ({ children, className = "", ...rest }) => (
  <div {...rest} className={`bg-white rounded-2xl border border-[#E8DDD5] p-5 shadow-[0_4px_20px_-4px_rgba(110,45,56,0.06)] ${className}`}>
    {children}
  </div>
);

function Facts({ facts }) {
  return (
    <Card data-testid="analysis-facts-card">
      <ul className="space-y-3">
        {facts.map((f, i) => (
          <li key={i} className="flex gap-3 text-[15px] text-[#2B1E1B] leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#6E2D38] flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const Tag = ({ label, color }) => (
  <span className="text-[10px] tracking-[0.16em] uppercase font-bold" style={{ color }}>{label}</span>
);

function FactsVsInterpretations({ rows }) {
  return rows.map((r, i) => (
    <Card key={i} data-testid={`analysis-fvi-${i}`} className="grid gap-3">
      <div><Tag label="Fait" color="#2E6B4F" /><p className="text-[15px] text-[#2B1E1B] mt-0.5">« {r.fact} »</p></div>
      <div className="border-t border-dashed border-[#E8DDD5] pt-3"><Tag label="Interprétation" color="#A83A3A" /><p className="text-[15px] text-[#5C4A45] mt-0.5 italic">« {r.interpretation} »</p></div>
      <div className="border-t border-dashed border-[#E8DDD5] pt-3"><Tag label="Hypothèse" color="#8A6D3B" /><p className="text-[15px] text-[#4A3B37] mt-0.5">{r.hypothesis}</p></div>
    </Card>
  ));
}

function Hypotheses({ items }) {
  return items.map((h, i) => (
    <Card key={i} data-testid={`analysis-hypothesis-${i}`}>
      <p className="font-editorial text-lg font-semibold text-[#2B1E1B] mb-3">{h.title}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold text-[#2E6B4F] mb-1.5">Ce qui va dans ce sens</p>
          <ul className="space-y-1.5 text-sm text-[#4A3B37]">{h.supporting.map((s, j) => <li key={j}>· {s}</li>)}</ul>
        </div>
        <div>
          <p className="text-xs font-semibold text-[#8A6D3B] mb-1.5">Ce qui empêche d'en être sûre</p>
          <ul className="space-y-1.5 text-sm text-[#4A3B37]">{h.limits.map((s, j) => <li key={j}>· {s}</li>)}</ul>
        </div>
      </div>
    </Card>
  ));
}

function Flags({ red, green }) {
  const Group = ({ items, label, color, bg, testid }) => (
    <Card data-testid={testid} className="border-0" style={{ background: bg }}>
      <p className="text-xs tracking-[0.16em] uppercase font-bold mb-3" style={{ color }}>{label}</p>
      <ul className="space-y-3">
        {items.map((f, i) => (
          <li key={i}>
            <p className="font-semibold text-[#2B1E1B] text-[15px]">{f.title}</p>
            <p className="text-sm text-[#5C4A45] leading-relaxed">{f.why}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
  return (
    <>
      <Group items={red} label="Red flags — à surveiller" color="#A83A3A" bg="#FDF0F0" testid="analysis-red-flags" />
      <Group items={green} label="Green flags — ce qui tient" color="#2E6B4F" bg="#EEF5F0" testid="analysis-green-flags" />
    </>
  );
}

function Answer({ question, answer }) {
  return (
    <Card data-testid="analysis-answer-card" className="border-[#C48B92]/50 bg-[#FFFDF9]">
      <div className="flex gap-2 items-start mb-4">
        <Quote className="w-4 h-4 text-[#C48B92] mt-1 flex-shrink-0" />
        <p className="font-editorial text-lg text-[#6E2D38] italic leading-snug">{question}</p>
      </div>
      <p className="text-[17px] font-semibold text-[#2B1E1B] leading-relaxed mb-4" data-testid="analysis-answer-summary">{answer.summary}</p>
      <div className="space-y-3 text-[15px] text-[#4A3B37] leading-relaxed">{answer.detail.map((p, i) => <p key={i}>{p}</p>)}</div>
    </Card>
  );
}

function ActionPlan({ items }) {
  return items.map((a, i) => (
    <Card key={i} data-testid={`analysis-action-${i}`} className="flex gap-4">
      <div className="w-8 h-8 rounded-full bg-[#6E2D38] text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">{i + 1}</div>
      <div className="min-w-0">
        <p className="font-semibold text-[#2B1E1B] text-[15px]">{a.title}</p>
        <p className="text-sm text-[#5C4A45] leading-relaxed mt-1">{a.detail}</p>
        {a.script && (
          <div className="mt-3 rounded-xl bg-[#F2E9E1] px-4 py-3 text-sm text-[#2B1E1B] leading-relaxed">
            <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#8C6B64] mb-1">Une formulation possible</p>
            {a.script}
          </div>
        )}
      </div>
    </Card>
  ));
}

function KnownUnknown({ known, unknown }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Card data-testid="analysis-known" className="bg-[#EEF5F0] border-0">
        <p className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase font-bold text-[#2E6B4F] mb-3"><CheckCircle2 className="w-4 h-4" /> Ce que nous savons</p>
        <ul className="space-y-2 text-[15px] text-[#2B1E1B]">{known.map((k, i) => <li key={i} className="flex gap-2"><span>—</span><span>{k}</span></li>)}</ul>
      </Card>
      <Card data-testid="analysis-unknown" className="bg-[#F7F1E8] border-0">
        <p className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase font-bold text-[#8A6D3B] mb-3"><HelpCircle className="w-4 h-4" /> Ce que nous ne savons pas encore</p>
        <ul className="space-y-2 text-[15px] text-[#2B1E1B]">{unknown.map((k, i) => <li key={i} className="flex gap-2"><span>—</span><span>{k}</span></li>)}</ul>
      </Card>
    </div>
  );
}

function useActiveSection() {
  const [active, setActive] = useState(SECTIONS[0].id);
  useEffect(() => {
    const onScroll = () => {
      const line = window.innerHeight * 0.3;
      let current = SECTIONS[0].id;
      SECTIONS.forEach((s) => {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= line) current = s.id;
      });
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.querySelector(`[data-testid="analysis-nav-${active}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [active]);
  return active;
}

export const AnalysisReport = ({ analysis: a }) => {
  const active = useActiveSection();
  return (
    <article className="max-w-2xl mx-auto px-4 pb-16" data-testid="analysis-report">
      <header className="pt-8 pb-6">
        <p className="text-[11px] tracking-[0.2em] uppercase text-[#B08A82] font-semibold mb-2">Analyse RawLove · {a.generated_at}</p>
        <h1 className="font-editorial text-4xl sm:text-5xl font-semibold leading-[1.05] text-[#2B1E1B]" data-testid="analysis-title">
          {a.first_name}, voici ce que j'ai compris.
        </h1>
        <div className="mt-5 flex gap-3 items-start bg-white rounded-2xl border border-[#E8DDD5] p-4">
          <img src={MARYLINE_AVATAR} alt="Maryline" className="w-10 h-10 rounded-full object-cover border border-[#E8DDD5] flex-shrink-0" />
          <p className="text-[15px] text-[#4A3B37] leading-relaxed" data-testid="analysis-intro">{a.intro}</p>
        </div>
      </header>

      <SectionNav active={active} />

      <div className="space-y-12 pt-8">
        <Section meta={SECTIONS[0]} lead="Uniquement ce que tu as raconté ou observé. Aucune interprétation ici.">
          <Facts facts={a.facts} />
        </Section>
        <Section meta={SECTIONS[1]} lead="Ce que tu sais, ce que tu supposes, et ce qu'on peut raisonnablement en tirer.">
          <FactsVsInterpretations rows={a.facts_vs_interpretations} />
        </Section>
        <Section meta={SECTIONS[2]} lead="Plusieurs explications restent possibles. Aucune n'est une vérité.">
          <Hypotheses items={a.hypotheses} />
        </Section>
        <Section meta={SECTIONS[3]} lead="Des comportements concrets qui méritent ton attention — pas un jugement sur une personne.">
          <Flags red={a.red_flags} green={a.green_flags} />
        </Section>
        <Section meta={SECTIONS[4]} tint="#F5E1E4">
          <Answer question={a.central_question} answer={a.answer} />
        </Section>
        <Section meta={SECTIONS[5]} lead="Peu d'actions, mais concrètes et adaptées à ton histoire.">
          <ActionPlan items={a.action_plan} />
        </Section>
        <Section meta={SECTIONS[6]} lead="Retrouver de la clarté, sans transformer des suppositions en certitudes.">
          <KnownUnknown known={a.known} unknown={a.unknown} />
        </Section>

        {a.safety && (
          <Card data-testid="analysis-safety" className="bg-[#FDF0F0] border-[#A83A3A]/25 flex gap-3">
            <ShieldAlert className="w-5 h-5 text-[#A83A3A] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#7a2b2b] leading-relaxed">{a.safety}</p>
          </Card>
        )}

        <footer className="flex gap-3 items-start pt-4 border-t border-[#E8DDD5]" data-testid="analysis-closing">
          <img src={MARYLINE_AVATAR} alt="Maryline" className="w-10 h-10 rounded-full object-cover border border-[#E8DDD5] flex-shrink-0" />
          <div>
            <p className="font-editorial text-lg text-[#2B1E1B] leading-relaxed">{a.closing}</p>
            <p className="text-sm text-[#8C6B64] mt-2">— Maryline</p>
          </div>
        </footer>
      </div>
    </article>
  );
};
